import React from 'react';

const getAbsolutePdfUrl = (pdfUrl) => {
  if (!pdfUrl) return null;
  if (pdfUrl.startsWith('http://') || pdfUrl.startsWith('https://')) {
    return pdfUrl;
  }
  const prefix = pdfUrl.startsWith('/') ? '' : '/';
  return `${window.location.origin}${prefix}${pdfUrl}`;
};

const ObituaryDisplay = ({ data, onNewObituary }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const calculateAge = (birthDate, deathDate) => {
    if (!birthDate || !deathDate) return data.age || '';
    const birth = new Date(birthDate);
    const death = new Date(deathDate);
    const age = death.getFullYear() - birth.getFullYear();
    const monthDiff = death.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && death.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const fullName = () => {
    const parts = [data.firstName, data.middleName, data.lastName].filter(Boolean);
    return parts.join(' ');
  };

  const age = calculateAge(data.dateOfBirth, data.dateOfDeath) || data.age;
  const pdfUrl = data.pdf_url || data.pdfUrl;
  const handleDownloadPdf = () => {
    const url = getAbsolutePdfUrl(pdfUrl);
    if (!url) return;
    const downloadWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (downloadWindow) {
      downloadWindow.focus();
    }
  };
  
  // Check if we have AI-generated obituary text
  const obituaryText = data.obituaryText || data.full_text;

  return (
    <div className="glass-card">
      <div className="obituary-header">
        <div className="obituary-name">{fullName()}</div>
        <div className="obituary-dates">
          {formatDate(data.dateOfBirth)} - {formatDate(data.dateOfDeath)}
          {age && ` (Age ${age})`}
        </div>
      </div>

      <div className="obituary-content">
        {obituaryText ? (
          // Display AI-generated obituary text
          <div className="obituary-section" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.8' }}>
            {obituaryText.split('\n\n').map((paragraph, index) => (
              <p key={index} style={{ marginBottom: '1.5rem' }}>
                {paragraph}
              </p>
            ))}
          </div>
        ) : (
          // Fallback to structured display if no AI text
          <>
            <div className="obituary-section">
              <p>
                {fullName()} {data.placeOfDeath ? `passed away ${data.placeOfDeath ? `in ${data.placeOfDeath}` : ''}` : 'has passed away'}
                {data.dateOfDeath ? ` on ${formatDate(data.dateOfDeath)}` : ''}.
                {data.placeOfBirth && ` Born ${data.placeOfBirth ? `in ${data.placeOfBirth}` : ''}`}
                {data.dateOfBirth ? ` on ${formatDate(data.dateOfBirth)}` : ''}, {fullName()} 
                {age ? ` lived a full life of ${age} years` : ' lived a meaningful life'}.
              </p>
            </div>

            {data.occupation && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Professional Life</h3>
                <p>
                  {fullName()} {data.education ? `was educated ${data.education}` : 'was a dedicated professional'}.
                  {data.occupation && ` They worked as ${data.occupation}`}.
                  {data.achievements && ` Throughout their career, ${data.achievements}`}.
                </p>
              </div>
            )}

            {data.militaryService && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Military Service</h3>
                <p>
                  {fullName()} proudly served {data.militaryService}. Their service to their country was a source of great pride and honor.
                </p>
              </div>
            )}

            {data.hobbies && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Personal Interests</h3>
                <p>
                  {fullName()} had a passion for {data.hobbies}. These interests brought joy and fulfillment throughout their life.
                </p>
              </div>
            )}

            {(data.familyMembers || data.survivedBy) && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Family</h3>
                <p>
                  {fullName()} is survived by {data.survivedBy || data.familyMembers}. They will be deeply missed by all who knew and loved them.
                </p>
              </div>
            )}

            {data.precededBy && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Preceded in Death</h3>
                <p>
                  {fullName()} was preceded in death by {data.precededBy}.
                </p>
              </div>
            )}

            {data.personalMessage && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">In Remembrance</h3>
                <p>
                  {data.personalMessage}
                </p>
              </div>
            )}

            {data.favoriteQuote && (
              <div className="obituary-section">
                <p style={{ fontStyle: 'italic', fontSize: '1.15rem', textAlign: 'center', marginTop: '2rem' }}>
                  "{data.favoriteQuote}"
                </p>
              </div>
            )}

            {(data.memorialService || data.serviceDate || data.serviceLocation) && (
              <div className="obituary-section">
                <h3 className="obituary-section-title">Memorial Services</h3>
                <p>
                  {data.memorialService || (data.serviceDate && data.serviceLocation ? 
                    `A memorial service will be held on ${formatDate(data.serviceDate)} at ${data.serviceLocation}` :
                    data.serviceDate ? `A memorial service will be held on ${formatDate(data.serviceDate)}` :
                    data.serviceLocation ? `A memorial service will be held at ${data.serviceLocation}` : '')}
                </p>
              </div>
            )}
          </>
        )}

        <div className="obituary-section" style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '2px solid rgba(255, 255, 255, 0.2)' }}>
          <p style={{ textAlign: 'center', fontSize: '1.1rem' }}>
            May they rest in peace and be forever remembered in our hearts.
          </p>
        </div>
      </div>

      <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 flex-wrap mt-4 text-center">
        {pdfUrl && (
          <button 
            type="button"
            className="btn btn-primary btn-lg px-4 shadow-sm"
            onClick={handleDownloadPdf}
          >
            <i className="bi bi-download me-2"></i>
            Download PDF
          </button>
        )}
        <button 
          type="button"
          className="btn btn-primary btn-lg px-4 shadow-sm"
          onClick={() => window.print()}
        >
          Print Obituary
        </button>
        <button 
          type="button"
          className="btn btn-lg px-4 shadow-sm"
          style={{
            backgroundColor: 'rgb(100, 100, 100)',
            borderColor: 'rgb(100, 100, 100)',
            color: '#ffffff',
            borderWidth: '1px',
            fontWeight: 600
          }}
          onClick={onNewObituary}
        >
          Create New Obituary
        </button>
      </div>
    </div>
  );
};

export default ObituaryDisplay;




